import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import IvisoraLogo from '../components/shared/IvisoraLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ForgotPasswordPage = () => {
  const [email, setEmail]     = useState('');
  const [status, setStatus]   = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (res.status === 503) {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.detail || 'Email service is unavailable. Please contact support.');
        setStatus('error');
        return;
      }

      // Always show success to avoid user enumeration (backend mirrors this)
      setStatus('success');
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f0ff 0%, #faf5ff 50%, #fff 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '20px',
        boxShadow: '0 8px 40px rgba(79,70,229,0.10)',
        padding: '48px 40px',
        width: '100%',
        maxWidth: '420px',
      }}>
        {/* Logo */}
        <Link to="/" aria-label="iVisora — home" style={{ display: 'inline-flex', alignItems: 'center', marginBottom: 32, textDecoration: 'none' }}>
          <IvisoraLogo className="h-8" />
        </Link>

        {status === 'success' ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: '#f0fdf4', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 20px',
            }}>
              <CheckCircle size={32} color="#16a34a" />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              Check your inbox
            </h2>
            <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6, marginBottom: 28 }}>
              If an account with <strong>{email}</strong> exists, we've sent a
              password reset link. It expires in <strong>30 minutes</strong>.
            </p>
            <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 24 }}>
              Didn't get it? Check your spam folder or{' '}
              <button
                onClick={() => setStatus('idle')}
                style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontWeight: 600, fontSize: 13, padding: 0 }}
              >
                try again
              </button>.
            </p>
            <Link
              to="/"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                color: '#6b7280', fontSize: 14, textDecoration: 'none',
              }}
            >
              <ArrowLeft size={14} /> Back to home
            </Link>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              Forgot your password?
            </h1>
            <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6, marginBottom: 28 }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {status === 'error' && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 10, padding: '12px 14px', marginBottom: 20,
              }}>
                <AlertCircle size={16} color="#dc2626" style={{ marginTop: 1, flexShrink: 0 }} />
                <p style={{ color: '#dc2626', fontSize: 13, margin: 0 }}>{errorMsg}</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Email address
              </label>
              <div style={{ position: 'relative', marginBottom: 24 }}>
                <Mail size={16} color="#9ca3af" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '11px 14px 11px 40px',
                    border: '1.5px solid #e5e7eb', borderRadius: 10,
                    fontSize: 14, color: '#111827', outline: 'none',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#4f46e5')}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                />
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                style={{
                  width: '100%', padding: '12px',
                  background: status === 'loading' ? '#a5b4fc' : 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                  color: '#fff', border: 'none', borderRadius: 10,
                  fontSize: 15, fontWeight: 600, cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'opacity 0.15s',
                }}
              >
                {status === 'loading' ? (
                  <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Sending…</>
                ) : 'Send reset link'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Link
                to="/"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6b7280', fontSize: 14, textDecoration: 'none' }}
              >
                <ArrowLeft size={14} /> Back to home
              </Link>
            </div>
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ForgotPasswordPage;
